import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import TextInputComponent from '../../../../components/input/TextInputComponent';
import SelectComponent from '../../../../components/select/SelectComponent';
import ExpiryGroupCard from './ExpiryGroupCard';

// ---------------------------------------------------------------------------
// Rule parsing / generation helpers
// ---------------------------------------------------------------------------

const parseRuleForCertIds = (ruleCode) => {
  if (!ruleCode) return [];
  const regex = /ProductCatalogueId\s*==\s*(\d+)/g;
  const ids = new Set();
  let match;
  while ((match = regex.exec(ruleCode)) !== null) {
    ids.add(parseInt(match[1], 10));
  }
  return Array.from(ids);
};

const DEFAULT_EXPIRY_GROUP = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  name: '',
  certIds: [],
  includeExpiry: true,
  includeIsActive: false,
  includeApproved: false,
});

const parseExpiryRuleGroups = (ruleCode) => {
  if (!ruleCode) return { groups: [], fallbackDays: 90, useFallback: true };

  const hasFallback =
    /DefaultIfEmpty|(?:\?\?\s*DateOnly\.FromDateTime)/.test(ruleCode) &&
    !/DateOnly\.MaxValue/.test(ruleCode);

  let fallbackDays = 90;
  const fallbackMatch = ruleCode.match(/AddDays\((\d+)\)/);
  if (fallbackMatch) fallbackDays = parseInt(fallbackMatch[1], 10);

  const groupSegments = ruleCode.split(/certificates\.Where\(c\s*=>\s*/);
  const groups = [];
  for (let i = 1; i < groupSegments.length; i++) {
    const seg = groupSegments[i];
    const certIds = parseRuleForCertIds(seg);
    if (certIds.length === 0) continue;
    groups.push({
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}-${i}`,
      name: `Group ${i}`,
      certIds,
      includeExpiry: /ExpiryDate\s*>\s*DateOnly/i.test(seg),
      includeIsActive: /c\.IsActive/i.test(seg),
      includeApproved: /ApprovalStatus\s*==\s*"Approved"/i.test(seg),
    });
  }
  return { groups, fallbackDays, useFallback: hasFallback };
};

const generateExpiryRuleCode = (groups, fallbackDays, useFallback = true) => {
  const validGroups = groups.filter((g) => g.certIds.length > 0);
  if (validGroups.length === 0) return '';

  const groupExpressions = validGroups.map((g) => {
    const conditions = g.certIds.map((id) => `c.ProductCatalogueId == ${id}`).join(' || ');
    const extra = [];
    if (g.includeApproved) extra.push('c.ApprovalStatus == "Approved"');
    if (g.includeIsActive) extra.push('c.IsActive');
    if (g.includeExpiry) extra.push('c.ExpiryDate > DateOnly.FromDateTime(DateTime.UtcNow)');
    const suffix = extra.length > 0 ? ` && ${extra.join(' && ')}` : '';
    return `certificates.Where(c => (${conditions})${suffix}).Select(c => (DateOnly?)c.ExpiryDate).Max()`;
  });

  const days = fallbackDays || 90;
  const fallbackExpr = `DateOnly.FromDateTime(DateTime.UtcNow.AddDays(${days}))`;

  if (validGroups.length === 1) {
    return useFallback
      ? `${groupExpressions[0]} ?? ${fallbackExpr}`
      : `${groupExpressions[0]} ?? DateOnly.MaxValue`;
  }

  return (
    `new[] { ${groupExpressions.join(', ')} }` +
    `.Where(d => d.HasValue).Select(d => d.Value)` +
    (useFallback ? `.DefaultIfEmpty(${fallbackExpr})` : '') +
    `.Min()`
  );
};

const getTeamId = (rule) => rule?.teamId ?? rule?.teamID;

const defaultForm = () => ({
  ruleName: 'Default Expiry Rule',
  ruleCode: 'DateOnly.FromDateTime(DateTime.Now.AddDays(90))',
  ruleEnabled: true,
  description: '',
  groups: [],
  fallbackDays: 90,
  useFallback: true,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ExpiryRuleBuilder({
  teams,
  certificates,
  getCertNameById,
  expiryRulesForSite,
  onValidate,
  onSave,
  onDelete,
  verifying,
  loadingExpiryRule,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [scopeTeamIds, setScopeTeamIds] = useState([]);
  const [templateTeamId, setTemplateTeamId] = useState('');
  const [ruleName, setRuleName] = useState('Default Expiry Rule');
  const [ruleCode, setRuleCode] = useState('DateOnly.FromDateTime(DateTime.Now.AddDays(90))');
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [description, setDescription] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  const [groups, setGroups] = useState([]);
  const [fallbackDays, setFallbackDays] = useState(90);
  const [useFallback, setUseFallback] = useState(true);
  const [groupSearchFilters, setGroupSearchFilters] = useState({});
  const [groupCustomIds, setGroupCustomIds] = useState({});
  const [showRawCode, setShowRawCode] = useState(false);

  const rules = useMemo(() => (Array.isArray(expiryRulesForSite) ? expiryRulesForSite : []), [
    expiryRulesForSite,
  ]);

  const rulesByTeam = useMemo(() => {
    const m = new Map();
    for (const r of rules) {
      const tid = getTeamId(r);
      if (tid != null) m.set(String(tid), r);
    }
    return m;
  }, [rules]);

  const ruleConflict = useMemo(() => {
    if (rules.length <= 1) return false;
    const codes = new Set(rules.map((r) => (r.ruleCode || '').trim()));
    return codes.size > 1;
  }, [rules]);

  const templateSelectItems = useMemo(() => {
    const withRule = rules.map((r) => getTeamId(r)).filter((id) => id != null);
    return [
      { id: '', name: 'Choose team template…' },
      ...withRule.map((tid) => {
        const t = teams.find((x) => String(x.id) === String(tid));
        return { id: tid, name: t?.name ?? `Team ${tid}` };
      }),
    ];
  }, [rules, teams]);

  const applyRuleToEditor = useCallback((rule) => {
    if (!rule) {
      const d = defaultForm();
      setRuleName(d.ruleName);
      setRuleCode(d.ruleCode);
      setRuleEnabled(d.ruleEnabled);
      setDescription('');
      setIsVerified(false);
      setGroups([]);
      setFallbackDays(d.fallbackDays);
      setUseFallback(d.useFallback);
      return;
    }
    setRuleName(rule.name || 'Default Expiry Rule');
    const code = rule.ruleCode || 'DateOnly.FromDateTime(DateTime.Now.AddDays(90))';
    setRuleCode(code);
    setRuleEnabled(rule.isEnabled !== false);
    setDescription(rule.description || '');
    setIsVerified(true);
    const parsed = parseExpiryRuleGroups(code);
    setGroups(parsed.groups);
    setFallbackDays(parsed.fallbackDays);
    setUseFallback(parsed.useFallback);
  }, []);

  // Hydrate from server rules when data or teams change
  useEffect(() => {
    if (loadingExpiryRule) return;

    const validTeamIds = new Set(teams.map((t) => String(t.id)));

    if (rules.length === 0) {
      applyRuleToEditor(null);
      setScopeTeamIds(teams.map((t) => t.id));
      setTemplateTeamId('');
      setGroupSearchFilters({});
      setGroupCustomIds({});
      return;
    }

    const teamIdsWithRule = rules.map((r) => getTeamId(r)).filter((id) => id != null);
    const scoped = teamIdsWithRule.filter((id) => validTeamIds.has(String(id)));
    setScopeTeamIds(scoped.length ? scoped : teamIdsWithRule);

    const firstTid = teamIdsWithRule[0];
    setTemplateTeamId(firstTid != null ? firstTid : '');

    const template = rules.find((r) => String(getTeamId(r)) === String(firstTid)) || rules[0];
    applyRuleToEditor(template);
    setGroupSearchFilters({});
    setGroupCustomIds({});
  }, [rules, teams, loadingExpiryRule, applyRuleToEditor]);

  const handleTemplateTeamChange = (v) => {
    setTemplateTeamId(v);
    const r = rulesByTeam.get(String(v));
    if (r) applyRuleToEditor(r);
  };

  useEffect(() => {
    setIsVerified(false);
  }, [ruleCode]);

  useEffect(() => {
    setScopeTeamIds((prev) => {
      if (prev.length === 0) return prev;
      const valid = new Set(teams.map((t) => String(t.id)));
      return prev.filter((id) => valid.has(String(id)));
    });
  }, [teams]);

  const toggleScopeTeam = (teamId) => {
    setScopeTeamIds((prev) => {
      const idStr = String(teamId);
      const has = prev.some((x) => String(x) === idStr);
      if (has && prev.length === 1) {
        enqueueSnackbar('Select at least one team', { variant: 'warning' });
        return prev;
      }
      if (has) return prev.filter((x) => String(x) !== idStr);
      return [...prev, teamId];
    });
  };

  const selectAllScope = () => {
    setScopeTeamIds(teams.map((t) => t.id));
  };

  const scopeOnlyTeamsWithExistingRule = () => {
    const ids = rules.map((r) => getTeamId(r)).filter((id) => id != null);
    setScopeTeamIds(ids);
  };

  const regenerateCode = (nextGroups, days, fb) => {
    const code = generateExpiryRuleCode(
      nextGroups,
      days ?? fallbackDays,
      fb !== undefined ? fb : useFallback
    );
    setRuleCode(code);
    setIsVerified(false);
  };

  const handleAddGroup = () => {
    const g = DEFAULT_EXPIRY_GROUP();
    g.name = `Group ${groups.length + 1}`;
    setGroups([...groups, g]);
  };

  const handleRemoveGroup = (id) => {
    const next = groups.filter((g) => g.id !== id);
    setGroups(next);
    regenerateCode(next);
  };

  const updateGroup = (id, updater) => {
    setGroups((prev) => {
      const next = prev.map((g) => (g.id === id ? updater(g) : g));
      regenerateCode(next);
      return next;
    });
  };

  const handleFallbackDaysChange = (value) => {
    const days = parseInt(value, 10) || 90;
    setFallbackDays(days);
    if (groups.length > 0) regenerateCode(groups, days, useFallback);
  };

  const handleUseFallbackToggle = (checked) => {
    setUseFallback(checked);
    if (groups.length > 0) regenerateCode(groups, fallbackDays, checked);
  };

  const handleValidate = async () => {
    const result = await onValidate(ruleCode);
    setIsVerified(result);
  };

  const handleSave = () => {
    if (scopeTeamIds.length === 0) {
      enqueueSnackbar('Select at least one team for this expiry rule', { variant: 'warning' });
      return;
    }
    onSave({
      name: ruleName,
      description,
      ruleCode,
      isEnabled: ruleEnabled,
      targetTeamIds: scopeTeamIds,
    });
  };

  const handleParseFromCode = () => {
    const parsed = parseExpiryRuleGroups(ruleCode);
    setGroups(parsed.groups);
    setFallbackDays(parsed.fallbackDays);
    setUseFallback(parsed.useFallback);
  };

  const hasAnySavedRule = rules.length > 0;
  const editorDisabled = loadingExpiryRule || teams.length === 0;

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Default Expiry Date Rule"
        subheader={
          hasAnySavedRule ? 'Edit the site expiry rule and team scope' : 'Create a new expiry rule'
        }
      />
      <Box sx={{ p: 3 }}>
        {loadingExpiryRule && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Typography color="text.secondary">Loading expiry rules…</Typography>
          </Box>
        )}

        {!loadingExpiryRule && teams.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Skillko teams are available for this account. Add teams in Skillko first.
          </Alert>
        )}

        {!loadingExpiryRule && teams.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Teams this rule applies to
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={selectAllScope}
                disabled={teams.length <= 1}
              >
                Select all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={scopeOnlyTeamsWithExistingRule}
                disabled={rules.length === 0}
              >
                Only teams with a saved rule
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, columnGap: 2, mb: 2 }}>
              {teams.map((t) => (
                <FormControlLabel
                  key={t.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={scopeTeamIds.some((id) => String(id) === String(t.id))}
                      onChange={() => toggleScopeTeam(t.id)}
                      disabled={editorDisabled}
                    />
                  }
                  label={t.name}
                />
              ))}
            </Box>

            {ruleConflict && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Teams on this site have <strong>different</strong> saved expiry rule code. Choose
                which team’s rule to edit; saving will align all selected teams to this definition.
              </Alert>
            )}

            {ruleConflict && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid xs={12} md={8}>
                  <SelectComponent
                    label="Template team (rule to edit)"
                    value={templateTeamId ?? ''}
                    setValue={handleTemplateTeamChange}
                    items={templateSelectItems}
                    helperText="Pick which team’s stored rule loads into the editor."
                  />
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid xs={12}>
                <Alert severity="info">
                  Build the expiry rule using certificate groups. Each group finds the latest (max)
                  expiry among its matching certs, then the earliest (min) across all groups becomes
                  the profile expiry.
                </Alert>
              </Grid>

              <Grid xs={12} md={6}>
                <TextInputComponent
                  label="Rule Name"
                  value={ruleName}
                  setValue={setRuleName}
                  disabled={editorDisabled}
                />
              </Grid>
              <Grid xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={useFallback}
                        onChange={(e) => handleUseFallbackToggle(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Fallback</Typography>}
                  />
                  <TextField
                    label="Days"
                    type="number"
                    size="small"
                    value={fallbackDays}
                    onChange={(e) => handleFallbackDaysChange(e.target.value)}
                    disabled={!useFallback}
                    sx={{ width: 80 }}
                  />
                </Box>
              </Grid>
              <Grid xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">Enabled:</Typography>
                  <Switch
                    checked={ruleEnabled}
                    onChange={(e) => setRuleEnabled(e.target.checked)}
                    disabled={editorDisabled}
                  />
                </Box>
              </Grid>

              <Grid xs={12}>
                <TextInputComponent
                  label="Description (optional)"
                  value={description}
                  setValue={setDescription}
                  disabled={editorDisabled}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Certificate Groups ({groups.length})
                  </Typography>
                  <Button size="small" variant="outlined" onClick={handleAddGroup} disabled={editorDisabled}>
                    + Add Group
                  </Button>
                </Box>

                {groups.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No groups yet. Click &quot;+ Add Group&quot; to start building the expiry rule visually.
                  </Typography>
                )}

                {groups.map((group, idx) => (
                  <ExpiryGroupCard
                    key={group.id}
                    group={group}
                    groupIndex={idx}
                    certificates={certificates}
                    getCertNameById={getCertNameById}
                    searchFilter={groupSearchFilters[group.id] || ''}
                    customIdValue={groupCustomIds[group.id] || ''}
                    onSearchFilterChange={(v) =>
                      setGroupSearchFilters((prev) => ({ ...prev, [group.id]: v }))
                    }
                    onCustomIdChange={(v) =>
                      setGroupCustomIds((prev) => ({ ...prev, [group.id]: v }))
                    }
                    onAddCustomId={() => {
                      const raw = groupCustomIds[group.id] || '';
                      const id = parseInt(raw, 10);
                      if (isNaN(id) || id <= 0) return;
                      updateGroup(group.id, (g) =>
                        g.certIds.includes(id) ? g : { ...g, certIds: [...g.certIds, id] }
                      );
                      setGroupCustomIds((prev) => ({ ...prev, [group.id]: '' }));
                    }}
                    onNameChange={(name) =>
                      setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, name } : g)))
                    }
                    onToggleFlag={(flag, value) =>
                      updateGroup(group.id, (g) => ({ ...g, [flag]: value }))
                    }
                    onToggleCert={(certId) =>
                      updateGroup(group.id, (g) => ({
                        ...g,
                        certIds: g.certIds.includes(certId)
                          ? g.certIds.filter((id) => id !== certId)
                          : [...g.certIds, certId],
                      }))
                    }
                    onSelectAll={() =>
                      updateGroup(group.id, (g) => ({
                        ...g,
                        certIds: certificates.map((c) => c.productCatalogueId),
                      }))
                    }
                    onDeselectAll={() => updateGroup(group.id, (g) => ({ ...g, certIds: [] }))}
                    onRemove={() => handleRemoveGroup(group.id)}
                  />
                ))}
              </Grid>

              <Grid xs={12}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowRawCode(!showRawCode)}
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  {showRawCode ? 'Hide rule code' : 'Advanced: View / edit rule code'}
                </Button>
                <Collapse in={showRawCode}>
                  <TextInputComponent
                    label="Rule Code (C# expression returning DateOnly)"
                    value={ruleCode}
                    setValue={(v) => {
                      setRuleCode(v);
                      setIsVerified(false);
                    }}
                    disabled={editorDisabled}
                    multiline
                    minRows={4}
                    maxRows={20}
                    placeholder="DateOnly.FromDateTime(DateTime.Now.AddDays(90))"
                  />
                </Collapse>
                {!showRawCode && ruleCode && (
                  <Typography variant="caption" color="text.secondary">
                    Rule code generated ({ruleCode.length} chars)
                  </Typography>
                )}
              </Grid>

              <Grid xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  {ruleCode && groups.length === 0 && (
                    <Button variant="outlined" size="small" onClick={handleParseFromCode}>
                      Parse into groups
                    </Button>
                  )}
                  <LoadingButton
                    variant="outlined"
                    onClick={handleValidate}
                    loading={verifying}
                    disabled={!ruleCode || editorDisabled}
                  >
                    Validate Rule
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    onClick={handleSave}
                    loading={loadingExpiryRule}
                    disabled={!isVerified || !ruleName || !ruleCode || editorDisabled}
                  >
                    {hasAnySavedRule ? 'Save rule' : 'Create rule'}
                  </LoadingButton>
                  {hasAnySavedRule && (
                    <LoadingButton
                      variant="outlined"
                      color="error"
                      onClick={onDelete}
                      loading={loadingExpiryRule}
                    >
                      Delete all for site
                    </LoadingButton>
                  )}
                  {isVerified && (
                    <Alert severity="success" sx={{ py: 0, alignSelf: 'center' }}>
                      Rule validated
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Card>
  );
}
